/**
 * Data for SEO landing pages, comparison pages, and style-specific content pages.
 */

// ---------------------------------------------------------------------------
// Style landing pages (/apa, /mla, /chicago, etc.)
// ---------------------------------------------------------------------------

export interface StyleLandingPage {
  /** URL slug — this becomes the page path, e.g. "apa" → /apa */
  slug: string;
  /**
   * CSL slug used by the citation engine — MUST be a slug that exists in
   * `CITATION_STYLES` (or one of the three engine built-ins: apa, vancouver,
   * harvard1). Anything else silently 404s when the engine fetches the .csl
   * file, and the user gets "Citation style not found".
   *
   * Guarded by `src/lib/__tests__/style-landing.test.ts`.
   */
  cslSlug: string;
  /** Short display name */
  name: string;
  /** Full official name */
  fullName: string;
  /** Edition or version string */
  edition: string;
  /** How in-text citations work */
  citationSystem: "author-date" | "numbered" | "footnotes";
  /** What the end-of-paper list is called */
  referenceListName: string;
  /** Brief description of in-text format, e.g. "(Author, Year)" */
  inTextFormat: string;
  /** Academic fields that commonly use this style */
  fields: string[];
  /** One-sentence tagline for the hero section */
  tagline: string;
  /**
   * The word the style's home audience uses for the activity. Universities in the
   * UK, Ireland, Australia, New Zealand, South Africa, India and much of the
   * Commonwealth say "referencing" and "reference list"; North America says
   * "citation". Harvard, OSCOLA, AGLC and Vancouver are searched for almost
   * entirely with the former, so their pages title themselves that way while still
   * describing citations in the copy. Omit for styles where "citation" is the norm.
   */
  terminology?: "referencing";
}

export const STYLE_LANDING_PAGES: StyleLandingPage[] = [
  {
    slug: "apa",
    cslSlug: "apa",
    name: "APA",
    fullName: "American Psychological Association 7th Edition",
    edition: "7th edition",
    citationSystem: "author-date",
    referenceListName: "Reference List",
    inTextFormat: "(Author, Year)",
    fields: [
      "Psychology",
      "Education",
      "Social Sciences",
      "Nursing",
      "Business",
    ],
    tagline: "The most widely used citation style in the social sciences",
  },
  {
    slug: "mla",
    cslSlug: "modern-language-association",
    name: "MLA",
    fullName: "Modern Language Association 9th Edition",
    edition: "9th edition",
    citationSystem: "author-date",
    referenceListName: "Works Cited",
    inTextFormat: "(Author Page)",
    fields: [
      "Literature",
      "Languages",
      "Arts",
      "Humanities",
      "Cultural Studies",
    ],
    tagline: "The standard citation style for the humanities",
  },
  {
    slug: "chicago",
    cslSlug: "chicago-author-date",
    name: "Chicago",
    fullName: "Chicago Manual of Style 18th Edition",
    edition: "18th edition",
    citationSystem: "author-date",
    referenceListName: "Reference List",
    inTextFormat: "(Author Year)",
    fields: ["History", "Social Sciences", "Arts", "Humanities", "Publishing"],
    tagline:
      "America's most trusted style guide for authors, editors, and publishers",
  },
  {
    slug: "harvard",
    cslSlug: "harvard1",
    name: "Harvard",
    fullName: "Harvard Referencing (Cite Them Right 11th Edition)",
    edition: "Cite Them Right 11th edition",
    citationSystem: "author-date",
    referenceListName: "Reference List",
    inTextFormat: "(Author, Year)",
    fields: ["Business", "Social Sciences", "Sciences", "Law", "Engineering"],
    tagline:
      "The most popular referencing style in UK and Australian universities",
    terminology: "referencing",
  },
  {
    slug: "vancouver",
    cslSlug: "vancouver",
    name: "Vancouver",
    fullName: "Vancouver (ICMJE Recommendations)",
    edition: "ICMJE standard",
    citationSystem: "numbered",
    referenceListName: "References",
    inTextFormat: "Superscript or bracketed numbers (1)",
    fields: ["Medicine", "Nursing", "Health Sciences", "Biomedical Sciences"],
    tagline: "The standard citation style for medical and biomedical journals",
    terminology: "referencing",
  },
  {
    slug: "ieee",
    cslSlug: "ieee",
    name: "IEEE",
    fullName: "Institute of Electrical and Electronics Engineers",
    edition: "standard",
    citationSystem: "numbered",
    referenceListName: "References",
    inTextFormat: "Bracketed numbers [1]",
    fields: [
      "Engineering",
      "Computer Science",
      "Electronics",
      "Information Technology",
    ],
    tagline: "The go-to citation style for engineering and computer science",
  },
  {
    slug: "ama",
    cslSlug: "american-medical-association",
    name: "AMA",
    fullName: "American Medical Association 11th Edition",
    edition: "11th edition",
    citationSystem: "numbered",
    referenceListName: "References",
    inTextFormat: "Superscript numbers¹",
    fields: ["Medicine", "Health Sciences", "Pharmacy", "Public Health"],
    tagline: "The citation standard for American medical journals",
  },
  {
    slug: "turabian",
    cslSlug: "dependent-turabian-author-date",
    name: "Turabian",
    fullName: "A Manual for Writers (Turabian) 9th Edition",
    edition: "9th edition",
    citationSystem: "author-date",
    referenceListName: "Reference List",
    inTextFormat: "(Author Year)",
    fields: ["History", "Humanities", "Social Sciences", "Theology"],
    tagline: "The student-friendly version of Chicago style",
  },
  {
    slug: "cse",
    cslSlug: "taylor-and-francis-council-of-science-editors-author-date",
    name: "CSE",
    fullName: "Council of Science Editors 8th Edition",
    edition: "8th edition",
    citationSystem: "numbered",
    referenceListName: "References",
    inTextFormat: "Superscript numbers or (Author Year)",
    fields: ["Biology", "Ecology", "Geology", "Environmental Science"],
    tagline:
      "The preferred citation style for the natural and physical sciences",
  },
  {
    slug: "bibtex",
    cslSlug: "bibtex",
    name: "BibTeX",
    fullName: "BibTeX for LaTeX",
    edition: "standard",
    citationSystem: "author-date",
    referenceListName: "Bibliography",
    inTextFormat: "Key-based references",
    fields: ["Computer Science", "Mathematics", "Physics", "Engineering"],
    tagline: "The bibliography format for LaTeX users and academic typesetting",
  },
  {
    slug: "nature",
    cslSlug: "nature",
    name: "Nature",
    fullName: "Nature Journal Style",
    edition: "current",
    citationSystem: "numbered",
    referenceListName: "References",
    inTextFormat: "Superscript numbers¹",
    fields: ["Natural Sciences", "Biology", "Physics", "Chemistry", "Medicine"],
    tagline:
      "The citation style used by the world's leading multidisciplinary journal",
  },
  {
    slug: "acs",
    cslSlug: "american-chemical-society",
    name: "ACS",
    fullName: "American Chemical Society",
    edition: "current",
    citationSystem: "numbered",
    referenceListName: "References",
    inTextFormat: "Superscript numbers or (Author, Year)",
    fields: [
      "Chemistry",
      "Biochemistry",
      "Chemical Engineering",
      "Materials Science",
    ],
    tagline: "The citation standard for chemistry and chemical sciences",
  },
  {
    slug: "oscola",
    cslSlug: "oscola",
    name: "OSCOLA",
    fullName:
      "Oxford University Standard for the Citation of Legal Authorities",
    edition: "5th edition",
    citationSystem: "footnotes",
    referenceListName: "Bibliography",
    inTextFormat: "Footnotes with case and legislation citations",
    fields: ["Law", "Legal Studies", "Jurisprudence"],
    tagline:
      "The standard legal citation style for UK law schools and journals",
    terminology: "referencing",
  },
  {
    slug: "bluebook",
    cslSlug: "bluebook-law-review",
    name: "Bluebook",
    fullName: "The Bluebook: A Uniform System of Citation",
    edition: "22nd edition",
    citationSystem: "footnotes",
    referenceListName: "Table of Authorities",
    inTextFormat: "Footnotes with signal abbreviations",
    fields: ["Law", "Legal Studies", "Court Documents", "Law Review"],
    tagline:
      "The definitive legal citation system for US courts and law reviews",
  },
  {
    slug: "aglc",
    cslSlug: "australian-guide-to-legal-citation",
    name: "AGLC",
    fullName: "Australian Guide to Legal Citation 4th Edition",
    edition: "4th edition",
    citationSystem: "footnotes",
    referenceListName: "Bibliography",
    inTextFormat: "Footnotes with pinpoint references",
    fields: ["Australian Law", "Legal Studies"],
    tagline:
      "The standard legal citation style for Australian law schools and courts",
    terminology: "referencing",
  },
  {
    slug: "asa",
    cslSlug: "american-sociological-association",
    name: "ASA",
    fullName: "American Sociological Association 7th Edition",
    edition: "7th edition",
    citationSystem: "author-date",
    referenceListName: "References",
    inTextFormat: "(Author Year)",
    fields: ["Sociology", "Social Work", "Criminology", "Demography"],
    tagline: "The standard citation style for sociology and social research",
  },
  {
    slug: "apsa",
    cslSlug: "american-political-science-association",
    name: "APSA",
    fullName: "American Political Science Association",
    edition: "current",
    citationSystem: "author-date",
    referenceListName: "References",
    inTextFormat: "(Author Year)",
    fields: ["Political Science", "International Relations", "Public Policy"],
    tagline: "The citation style for political science research and journals",
  },
  {
    slug: "aaa",
    cslSlug: "american-anthropological-association",
    name: "AAA",
    fullName: "American Anthropological Association",
    edition: "current",
    citationSystem: "author-date",
    referenceListName: "References Cited",
    inTextFormat: "(Author Year)",
    fields: ["Anthropology", "Archaeology", "Ethnography"],
    tagline: "The standard citation style for anthropological research",
  },
  {
    slug: "nlm",
    cslSlug: "taylor-and-francis-national-library-of-medicine",
    name: "NLM",
    fullName: "National Library of Medicine",
    edition: "current",
    citationSystem: "numbered",
    referenceListName: "References",
    inTextFormat: "Bracketed numbers (1)",
    fields: ["Medicine", "Life Sciences", "Public Health"],
    tagline:
      "The citation standard used by PubMed and the National Library of Medicine",
  },
  {
    slug: "acm",
    cslSlug: "association-for-computing-machinery",
    name: "ACM",
    fullName: "Association for Computing Machinery",
    edition: "current",
    citationSystem: "numbered",
    referenceListName: "References",
    inTextFormat: "Bracketed numbers [1]",
    fields: ["Computer Science", "Information Systems", "Software Engineering"],
    tagline: "The citation style for ACM conferences and computing research",
  },
  {
    slug: "aps",
    cslSlug: "american-physical-society-et-al",
    name: "APS",
    fullName: "American Physical Society",
    edition: "current",
    citationSystem: "numbered",
    referenceListName: "References",
    inTextFormat: "Bracketed numbers [1]",
    fields: ["Physics", "Astrophysics", "Condensed Matter"],
    tagline: "The citation style for Physical Review and APS journals",
  },
  {
    slug: "aip",
    cslSlug: "american-institute-of-physics",
    name: "AIP",
    fullName: "American Institute of Physics",
    edition: "current",
    citationSystem: "numbered",
    referenceListName: "References",
    inTextFormat: "Superscript numbers¹",
    fields: ["Physics", "Applied Physics", "Acoustics"],
    tagline: "The citation style for AIP and physics journals",
  },
  {
    slug: "cell",
    cslSlug: "cell",
    name: "Cell",
    fullName: "Cell Journal Style",
    edition: "current",
    citationSystem: "author-date",
    referenceListName: "References",
    inTextFormat: "(Author, Year)",
    fields: ["Cell Biology", "Molecular Biology", "Genetics", "Biotechnology"],
    tagline: "The citation style for Cell Press and leading biology journals",
  },
  {
    slug: "science",
    cslSlug: "science",
    name: "Science",
    fullName: "Science (AAAS) Journal Style",
    edition: "current",
    citationSystem: "numbered",
    referenceListName: "References",
    inTextFormat: "Italic numbers (1)",
    fields: ["Multidisciplinary Science", "Biology", "Physics", "Chemistry"],
    tagline: "The citation style for Science magazine and AAAS journals",
  },
  {
    slug: "plos",
    cslSlug: "dependent-plos-one",
    name: "PLOS",
    fullName: "PLOS ONE Style",
    edition: "current",
    citationSystem: "numbered",
    referenceListName: "References",
    inTextFormat: "Bracketed numbers [1]",
    fields: [
      "Multidisciplinary Science",
      "Biology",
      "Medicine",
      "Public Health",
    ],
    tagline: "The citation style for PLOS open-access journals",
  },
  {
    slug: "bmj",
    cslSlug: "bmj",
    name: "BMJ",
    fullName: "British Medical Journal Style",
    edition: "current",
    citationSystem: "numbered",
    referenceListName: "References",
    inTextFormat: "Superscript numbers¹",
    fields: ["Medicine", "Public Health", "Epidemiology"],
    tagline: "The citation style for BMJ and leading British medical journals",
  },
  {
    slug: "lancet",
    cslSlug: "the-lancet",
    name: "Lancet",
    fullName: "The Lancet Journal Style",
    edition: "current",
    citationSystem: "numbered",
    referenceListName: "References",
    inTextFormat: "Superscript numbers¹",
    fields: ["Medicine", "Global Health", "Public Health"],
    tagline: "The citation style for The Lancet family of medical journals",
  },
  {
    slug: "jama",
    cslSlug: "dependent-jama",
    name: "JAMA",
    fullName: "Journal of the American Medical Association",
    edition: "current",
    citationSystem: "numbered",
    referenceListName: "References",
    inTextFormat: "Superscript numbers¹",
    fields: ["Medicine", "Clinical Research", "Public Health"],
    tagline: "The citation style for JAMA and the JAMA Network journals",
  },
  {
    slug: "elsevier-harvard",
    cslSlug: "elsevier-harvard",
    name: "Elsevier Harvard",
    fullName: "Elsevier Harvard Style",
    edition: "current",
    citationSystem: "author-date",
    referenceListName: "References",
    inTextFormat: "(Author, Year)",
    fields: ["Multidisciplinary", "Science", "Engineering", "Medicine"],
    tagline: "The Harvard-style variant used across Elsevier journals",
  },
  {
    slug: "springer",
    cslSlug: "springer-basic-author-date",
    name: "Springer",
    fullName: "Springer Basic Style",
    edition: "current",
    citationSystem: "numbered",
    referenceListName: "References",
    inTextFormat: "Bracketed numbers [1]",
    fields: ["Multidisciplinary", "Science", "Engineering", "Computer Science"],
    tagline: "The citation style for Springer Nature journals and books",
  },
];

export function getStyleLandingPage(
  slug: string
): StyleLandingPage | undefined {
  return STYLE_LANDING_PAGES.find((s) => s.slug === slug);
}

// ---------------------------------------------------------------------------
// Comparison page pairs
// ---------------------------------------------------------------------------

export interface ComparisonPair {
  slug: string;
  styleA: string;
  styleB: string;
  nameA: string;
  nameB: string;
}

const COMPARISON_PAIR_CANDIDATES: ComparisonPair[] = [
  {
    slug: "apa-vs-mla",
    styleA: "apa",
    styleB: "mla",
    nameA: "APA",
    nameB: "MLA",
  },
  {
    slug: "apa-vs-chicago",
    styleA: "apa",
    styleB: "chicago",
    nameA: "APA",
    nameB: "Chicago",
  },
  {
    slug: "mla-vs-chicago",
    styleA: "mla",
    styleB: "chicago",
    nameA: "MLA",
    nameB: "Chicago",
  },
  {
    slug: "apa-vs-harvard",
    styleA: "apa",
    styleB: "harvard",
    nameA: "APA",
    nameB: "Harvard",
  },
  {
    slug: "harvard-vs-vancouver",
    styleA: "harvard",
    styleB: "vancouver",
    nameA: "Harvard",
    nameB: "Vancouver",
  },
  {
    slug: "chicago-vs-turabian",
    styleA: "chicago",
    styleB: "turabian",
    nameA: "Chicago",
    nameB: "Turabian",
  },
  {
    slug: "apa-vs-vancouver",
    styleA: "apa",
    styleB: "vancouver",
    nameA: "APA",
    nameB: "Vancouver",
  },
  {
    slug: "apa-vs-ieee",
    styleA: "apa",
    styleB: "ieee",
    nameA: "APA",
    nameB: "IEEE",
  },
  {
    slug: "mla-vs-apa",
    styleA: "mla",
    styleB: "apa",
    nameA: "MLA",
    nameB: "APA",
  },
  {
    slug: "apa-vs-ama",
    styleA: "apa",
    styleB: "ama",
    nameA: "APA",
    nameB: "AMA",
  },
  {
    slug: "mla-vs-harvard",
    styleA: "mla",
    styleB: "harvard",
    nameA: "MLA",
    nameB: "Harvard",
  },
  {
    slug: "ieee-vs-vancouver",
    styleA: "ieee",
    styleB: "vancouver",
    nameA: "IEEE",
    nameB: "Vancouver",
  },
  {
    slug: "apa-vs-asa",
    styleA: "apa",
    styleB: "asa",
    nameA: "APA",
    nameB: "ASA",
  },
  {
    slug: "harvard-vs-apa",
    styleA: "harvard",
    styleB: "apa",
    nameA: "Harvard",
    nameB: "APA",
  },
  {
    slug: "vancouver-vs-ama",
    styleA: "vancouver",
    styleB: "ama",
    nameA: "Vancouver",
    nameB: "AMA",
  },
  {
    slug: "apa-vs-bluebook",
    styleA: "apa",
    styleB: "bluebook",
    nameA: "APA",
    nameB: "Bluebook",
  },
  {
    slug: "oscola-vs-bluebook",
    styleA: "oscola",
    styleB: "bluebook",
    nameA: "OSCOLA",
    nameB: "Bluebook",
  },
  {
    slug: "apa-vs-cse",
    styleA: "apa",
    styleB: "cse",
    nameA: "APA",
    nameB: "CSE",
  },
  {
    slug: "chicago-vs-harvard",
    styleA: "chicago",
    styleB: "harvard",
    nameA: "Chicago",
    nameB: "Harvard",
  },
  {
    slug: "apa-vs-nature",
    styleA: "apa",
    styleB: "nature",
    nameA: "APA",
    nameB: "Nature",
  },
  // --- Top-10 C(10,2) = 45 pairs (excluding those already above) ---
  {
    slug: "mla-vs-vancouver",
    styleA: "mla",
    styleB: "vancouver",
    nameA: "MLA",
    nameB: "Vancouver",
  },
  {
    slug: "mla-vs-ieee",
    styleA: "mla",
    styleB: "ieee",
    nameA: "MLA",
    nameB: "IEEE",
  },
  {
    slug: "mla-vs-ama",
    styleA: "mla",
    styleB: "ama",
    nameA: "MLA",
    nameB: "AMA",
  },
  {
    slug: "mla-vs-turabian",
    styleA: "mla",
    styleB: "turabian",
    nameA: "MLA",
    nameB: "Turabian",
  },
  {
    slug: "mla-vs-cse",
    styleA: "mla",
    styleB: "cse",
    nameA: "MLA",
    nameB: "CSE",
  },
  {
    slug: "mla-vs-nature",
    styleA: "mla",
    styleB: "nature",
    nameA: "MLA",
    nameB: "Nature",
  },
  {
    slug: "chicago-vs-vancouver",
    styleA: "chicago",
    styleB: "vancouver",
    nameA: "Chicago",
    nameB: "Vancouver",
  },
  {
    slug: "chicago-vs-ieee",
    styleA: "chicago",
    styleB: "ieee",
    nameA: "Chicago",
    nameB: "IEEE",
  },
  {
    slug: "chicago-vs-ama",
    styleA: "chicago",
    styleB: "ama",
    nameA: "Chicago",
    nameB: "AMA",
  },
  {
    slug: "chicago-vs-cse",
    styleA: "chicago",
    styleB: "cse",
    nameA: "Chicago",
    nameB: "CSE",
  },
  {
    slug: "chicago-vs-nature",
    styleA: "chicago",
    styleB: "nature",
    nameA: "Chicago",
    nameB: "Nature",
  },
  {
    slug: "harvard-vs-ieee",
    styleA: "harvard",
    styleB: "ieee",
    nameA: "Harvard",
    nameB: "IEEE",
  },
  {
    slug: "harvard-vs-ama",
    styleA: "harvard",
    styleB: "ama",
    nameA: "Harvard",
    nameB: "AMA",
  },
  {
    slug: "harvard-vs-turabian",
    styleA: "harvard",
    styleB: "turabian",
    nameA: "Harvard",
    nameB: "Turabian",
  },
  {
    slug: "harvard-vs-cse",
    styleA: "harvard",
    styleB: "cse",
    nameA: "Harvard",
    nameB: "CSE",
  },
  {
    slug: "harvard-vs-nature",
    styleA: "harvard",
    styleB: "nature",
    nameA: "Harvard",
    nameB: "Nature",
  },
  {
    slug: "vancouver-vs-ieee",
    styleA: "vancouver",
    styleB: "ieee",
    nameA: "Vancouver",
    nameB: "IEEE",
  },
  {
    slug: "vancouver-vs-turabian",
    styleA: "vancouver",
    styleB: "turabian",
    nameA: "Vancouver",
    nameB: "Turabian",
  },
  {
    slug: "vancouver-vs-cse",
    styleA: "vancouver",
    styleB: "cse",
    nameA: "Vancouver",
    nameB: "CSE",
  },
  {
    slug: "vancouver-vs-nature",
    styleA: "vancouver",
    styleB: "nature",
    nameA: "Vancouver",
    nameB: "Nature",
  },
  {
    slug: "ieee-vs-ama",
    styleA: "ieee",
    styleB: "ama",
    nameA: "IEEE",
    nameB: "AMA",
  },
  {
    slug: "ieee-vs-turabian",
    styleA: "ieee",
    styleB: "turabian",
    nameA: "IEEE",
    nameB: "Turabian",
  },
  {
    slug: "ieee-vs-cse",
    styleA: "ieee",
    styleB: "cse",
    nameA: "IEEE",
    nameB: "CSE",
  },
  {
    slug: "ieee-vs-nature",
    styleA: "ieee",
    styleB: "nature",
    nameA: "IEEE",
    nameB: "Nature",
  },
  {
    slug: "ama-vs-turabian",
    styleA: "ama",
    styleB: "turabian",
    nameA: "AMA",
    nameB: "Turabian",
  },
  {
    slug: "ama-vs-cse",
    styleA: "ama",
    styleB: "cse",
    nameA: "AMA",
    nameB: "CSE",
  },
  {
    slug: "ama-vs-nature",
    styleA: "ama",
    styleB: "nature",
    nameA: "AMA",
    nameB: "Nature",
  },
  {
    slug: "turabian-vs-cse",
    styleA: "turabian",
    styleB: "cse",
    nameA: "Turabian",
    nameB: "CSE",
  },
  {
    slug: "turabian-vs-nature",
    styleA: "turabian",
    styleB: "nature",
    nameA: "Turabian",
    nameB: "Nature",
  },
  {
    slug: "cse-vs-nature",
    styleA: "cse",
    styleB: "nature",
    nameA: "CSE",
    nameB: "Nature",
  },
  // --- Top styles vs law styles ---
  {
    slug: "apa-vs-oscola",
    styleA: "apa",
    styleB: "oscola",
    nameA: "APA",
    nameB: "OSCOLA",
  },
  {
    slug: "apa-vs-aglc",
    styleA: "apa",
    styleB: "aglc",
    nameA: "APA",
    nameB: "AGLC",
  },
  {
    slug: "mla-vs-bluebook",
    styleA: "mla",
    styleB: "bluebook",
    nameA: "MLA",
    nameB: "Bluebook",
  },
  {
    slug: "mla-vs-oscola",
    styleA: "mla",
    styleB: "oscola",
    nameA: "MLA",
    nameB: "OSCOLA",
  },
  {
    slug: "chicago-vs-bluebook",
    styleA: "chicago",
    styleB: "bluebook",
    nameA: "Chicago",
    nameB: "Bluebook",
  },
  {
    slug: "chicago-vs-oscola",
    styleA: "chicago",
    styleB: "oscola",
    nameA: "Chicago",
    nameB: "OSCOLA",
  },
  {
    slug: "harvard-vs-oscola",
    styleA: "harvard",
    styleB: "oscola",
    nameA: "Harvard",
    nameB: "OSCOLA",
  },
  {
    slug: "harvard-vs-bluebook",
    styleA: "harvard",
    styleB: "bluebook",
    nameA: "Harvard",
    nameB: "Bluebook",
  },
  {
    slug: "oscola-vs-aglc",
    styleA: "oscola",
    styleB: "aglc",
    nameA: "OSCOLA",
    nameB: "AGLC",
  },
  {
    slug: "bluebook-vs-aglc",
    styleA: "bluebook",
    styleB: "aglc",
    nameA: "Bluebook",
    nameB: "AGLC",
  },
  // --- Top styles vs medical styles ---
  {
    slug: "apa-vs-bmj",
    styleA: "apa",
    styleB: "bmj",
    nameA: "APA",
    nameB: "BMJ",
  },
  {
    slug: "apa-vs-lancet",
    styleA: "apa",
    styleB: "lancet",
    nameA: "APA",
    nameB: "Lancet",
  },
  {
    slug: "apa-vs-jama",
    styleA: "apa",
    styleB: "jama",
    nameA: "APA",
    nameB: "JAMA",
  },
  {
    slug: "vancouver-vs-bmj",
    styleA: "vancouver",
    styleB: "bmj",
    nameA: "Vancouver",
    nameB: "BMJ",
  },
  {
    slug: "vancouver-vs-lancet",
    styleA: "vancouver",
    styleB: "lancet",
    nameA: "Vancouver",
    nameB: "Lancet",
  },
  {
    slug: "vancouver-vs-jama",
    styleA: "vancouver",
    styleB: "jama",
    nameA: "Vancouver",
    nameB: "JAMA",
  },
  {
    slug: "ama-vs-vancouver",
    styleA: "ama",
    styleB: "vancouver",
    nameA: "AMA",
    nameB: "Vancouver",
  },
  {
    slug: "ama-vs-bmj",
    styleA: "ama",
    styleB: "bmj",
    nameA: "AMA",
    nameB: "BMJ",
  },
  {
    slug: "ama-vs-lancet",
    styleA: "ama",
    styleB: "lancet",
    nameA: "AMA",
    nameB: "Lancet",
  },
  {
    slug: "ama-vs-jama",
    styleA: "ama",
    styleB: "jama",
    nameA: "AMA",
    nameB: "JAMA",
  },
  {
    slug: "bmj-vs-lancet",
    styleA: "bmj",
    styleB: "lancet",
    nameA: "BMJ",
    nameB: "Lancet",
  },
  {
    slug: "bmj-vs-jama",
    styleA: "bmj",
    styleB: "jama",
    nameA: "BMJ",
    nameB: "JAMA",
  },
  {
    slug: "lancet-vs-jama",
    styleA: "lancet",
    styleB: "jama",
    nameA: "Lancet",
    nameB: "JAMA",
  },
  // --- Top styles vs science styles ---
  {
    slug: "apa-vs-acs",
    styleA: "apa",
    styleB: "acs",
    nameA: "APA",
    nameB: "ACS",
  },
  {
    slug: "apa-vs-cell",
    styleA: "apa",
    styleB: "cell",
    nameA: "APA",
    nameB: "Cell",
  },
  {
    slug: "apa-vs-science",
    styleA: "apa",
    styleB: "science",
    nameA: "APA",
    nameB: "Science",
  },
  {
    slug: "nature-vs-acs",
    styleA: "nature",
    styleB: "acs",
    nameA: "Nature",
    nameB: "ACS",
  },
  {
    slug: "nature-vs-cell",
    styleA: "nature",
    styleB: "cell",
    nameA: "Nature",
    nameB: "Cell",
  },
  {
    slug: "nature-vs-science",
    styleA: "nature",
    styleB: "science",
    nameA: "Nature",
    nameB: "Science",
  },
  {
    slug: "acs-vs-cell",
    styleA: "acs",
    styleB: "cell",
    nameA: "ACS",
    nameB: "Cell",
  },
  {
    slug: "acs-vs-science",
    styleA: "acs",
    styleB: "science",
    nameA: "ACS",
    nameB: "Science",
  },
  {
    slug: "cell-vs-science",
    styleA: "cell",
    styleB: "science",
    nameA: "Cell",
    nameB: "Science",
  },
  {
    slug: "cse-vs-acs",
    styleA: "cse",
    styleB: "acs",
    nameA: "CSE",
    nameB: "ACS",
  },
  {
    slug: "cse-vs-cell",
    styleA: "cse",
    styleB: "cell",
    nameA: "CSE",
    nameB: "Cell",
  },
  {
    slug: "cse-vs-science",
    styleA: "cse",
    styleB: "science",
    nameA: "CSE",
    nameB: "Science",
  },
  // --- Similar-style cross comparisons ---
  {
    slug: "elsevier-harvard-vs-harvard",
    styleA: "elsevier-harvard",
    styleB: "harvard",
    nameA: "Elsevier Harvard",
    nameB: "Harvard",
  },
  {
    slug: "elsevier-harvard-vs-apa",
    styleA: "elsevier-harvard",
    styleB: "apa",
    nameA: "Elsevier Harvard",
    nameB: "APA",
  },
  {
    slug: "turabian-vs-chicago",
    styleA: "turabian",
    styleB: "chicago",
    nameA: "Turabian",
    nameB: "Chicago",
  },
  {
    slug: "asa-vs-apa",
    styleA: "asa",
    styleB: "apa",
    nameA: "ASA",
    nameB: "APA",
  },
  {
    slug: "asa-vs-chicago",
    styleA: "asa",
    styleB: "chicago",
    nameA: "ASA",
    nameB: "Chicago",
  },
  {
    slug: "apsa-vs-apa",
    styleA: "apsa",
    styleB: "apa",
    nameA: "APSA",
    nameB: "APA",
  },
  {
    slug: "apsa-vs-chicago",
    styleA: "apsa",
    styleB: "chicago",
    nameA: "APSA",
    nameB: "Chicago",
  },
  {
    slug: "apsa-vs-asa",
    styleA: "apsa",
    styleB: "asa",
    nameA: "APSA",
    nameB: "ASA",
  },
  {
    slug: "nlm-vs-vancouver",
    styleA: "nlm",
    styleB: "vancouver",
    nameA: "NLM",
    nameB: "Vancouver",
  },
  {
    slug: "nlm-vs-ama",
    styleA: "nlm",
    styleB: "ama",
    nameA: "NLM",
    nameB: "AMA",
  },
  {
    slug: "acm-vs-ieee",
    styleA: "acm",
    styleB: "ieee",
    nameA: "ACM",
    nameB: "IEEE",
  },
  {
    slug: "springer-vs-ieee",
    styleA: "springer",
    styleB: "ieee",
    nameA: "Springer",
    nameB: "IEEE",
  },
  {
    slug: "springer-vs-nature",
    styleA: "springer",
    styleB: "nature",
    nameA: "Springer",
    nameB: "Nature",
  },
  {
    slug: "plos-vs-nature",
    styleA: "plos",
    styleB: "nature",
    nameA: "PLOS",
    nameB: "Nature",
  },
  {
    slug: "plos-vs-cell",
    styleA: "plos",
    styleB: "cell",
    nameA: "PLOS",
    nameB: "Cell",
  },
  {
    slug: "ieee-vs-acm",
    styleA: "ieee",
    styleB: "acm",
    nameA: "IEEE",
    nameB: "ACM",
  },
  {
    slug: "apa-vs-apsa",
    styleA: "apa",
    styleB: "apsa",
    nameA: "APA",
    nameB: "APSA",
  },
  {
    slug: "mla-vs-asa",
    styleA: "mla",
    styleB: "asa",
    nameA: "MLA",
    nameB: "ASA",
  },
  {
    slug: "harvard-vs-asa",
    styleA: "harvard",
    styleB: "asa",
    nameA: "Harvard",
    nameB: "ASA",
  },
  {
    slug: "chicago-vs-apsa",
    styleA: "chicago",
    styleB: "apsa",
    nameA: "Chicago",
    nameB: "APSA",
  },
];

function comparisonKey(pair: ComparisonPair): string {
  return [pair.styleA, pair.styleB].sort().join(":");
}

const canonicalComparisonByKey = new Map<string, ComparisonPair>();
const comparisonAliases = new Map<string, string>();

export const COMPARISON_PAIRS: ComparisonPair[] =
  COMPARISON_PAIR_CANDIDATES.filter((pair) => {
    const key = comparisonKey(pair);
    const canonical = canonicalComparisonByKey.get(key);
    if (canonical) {
      comparisonAliases.set(pair.slug, canonical.slug);
      return false;
    }
    canonicalComparisonByKey.set(key, pair);
    return true;
  });

/** Canonical slug for a historical reversed-pair URL, or the input when canonical. */
export function canonicalComparisonSlug(slug: string): string {
  return comparisonAliases.get(slug) ?? slug;
}

export function getComparisonPair(slug: string): ComparisonPair | undefined {
  return COMPARISON_PAIRS.find((p) => p.slug === slug);
}

// ---------------------------------------------------------------------------
// Academic field / discipline pages
// ---------------------------------------------------------------------------

export interface AcademicField {
  slug: string;
  name: string;
  description: string;
  commonStyles: string[];
  relatedFields: string[];
}

export const ACADEMIC_FIELDS: AcademicField[] = [
  {
    slug: "psychology",
    name: "Psychology",
    description:
      "Citation styles commonly used in psychology, behavioral science, and cognitive research.",
    commonStyles: ["apa", "apa"],
    relatedFields: ["education", "social-work", "neuroscience"],
  },
  {
    slug: "education",
    name: "Education",
    description:
      "Citation styles for education research, pedagogy, and curriculum studies.",
    commonStyles: ["apa"],
    relatedFields: ["psychology", "sociology"],
  },
  {
    slug: "sociology",
    name: "Sociology",
    description:
      "Citation styles used in sociological research and social theory.",
    commonStyles: ["asa", "apa", "chicago"],
    relatedFields: ["anthropology", "political-science", "social-work"],
  },
  {
    slug: "anthropology",
    name: "Anthropology",
    description:
      "Citation styles for anthropological and ethnographic research.",
    commonStyles: ["aaa", "chicago"],
    relatedFields: ["sociology", "archaeology", "history"],
  },
  {
    slug: "political-science",
    name: "Political Science",
    description:
      "Citation styles for political science, government, and public policy research.",
    commonStyles: ["apsa", "chicago", "apa"],
    relatedFields: ["sociology", "economics", "law"],
  },
  {
    slug: "economics",
    name: "Economics",
    description: "Citation styles used in economics and econometrics research.",
    commonStyles: ["chicago", "apa", "harvard"],
    relatedFields: ["business", "political-science", "statistics"],
  },
  {
    slug: "business",
    name: "Business & Management",
    description:
      "Citation styles for business, management, marketing, and finance research.",
    commonStyles: ["apa", "harvard", "chicago"],
    relatedFields: ["economics", "psychology"],
  },
  {
    slug: "literature",
    name: "Literature & Languages",
    description:
      "Citation styles for literary studies, linguistics, and language research.",
    commonStyles: ["mla", "chicago"],
    relatedFields: ["history", "philosophy", "cultural-studies"],
  },
  {
    slug: "history",
    name: "History",
    description:
      "Citation styles for historical research, historiography, and archival studies.",
    commonStyles: ["chicago", "turabian"],
    relatedFields: ["literature", "philosophy", "anthropology"],
  },
  {
    slug: "philosophy",
    name: "Philosophy",
    description:
      "Citation styles for philosophical writing and ethics research.",
    commonStyles: ["chicago", "mla"],
    relatedFields: ["history", "literature", "theology"],
  },
  {
    slug: "law",
    name: "Law & Legal Studies",
    description:
      "Legal citation styles for court documents, law reviews, and legal scholarship.",
    commonStyles: ["bluebook", "oscola", "aglc"],
    relatedFields: ["political-science", "criminology"],
  },
  {
    slug: "medicine",
    name: "Medicine & Clinical Sciences",
    description:
      "Citation styles for medical research, clinical studies, and healthcare.",
    commonStyles: ["vancouver", "ama", "nlm", "bmj", "lancet", "jama"],
    relatedFields: ["nursing", "public-health", "pharmacy"],
  },
  {
    slug: "nursing",
    name: "Nursing & Midwifery",
    description:
      "Citation styles for nursing research and healthcare practice.",
    commonStyles: ["apa", "vancouver"],
    relatedFields: ["medicine", "public-health"],
  },
  {
    slug: "public-health",
    name: "Public Health & Epidemiology",
    description:
      "Citation styles for public health research and epidemiological studies.",
    commonStyles: ["vancouver", "ama", "apa"],
    relatedFields: ["medicine", "nursing", "sociology"],
  },
  {
    slug: "pharmacy",
    name: "Pharmacy",
    description:
      "Citation styles for pharmaceutical and pharmacological research.",
    commonStyles: ["ama", "vancouver"],
    relatedFields: ["medicine", "chemistry"],
  },
  {
    slug: "biology",
    name: "Biology & Life Sciences",
    description:
      "Citation styles for biological research, ecology, and genetics.",
    commonStyles: ["cse", "nature", "cell", "plos"],
    relatedFields: ["ecology", "chemistry", "medicine"],
  },
  {
    slug: "chemistry",
    name: "Chemistry",
    description:
      "Citation styles for chemistry, biochemistry, and materials science research.",
    commonStyles: ["acs", "nature"],
    relatedFields: ["biology", "physics", "engineering"],
  },
  {
    slug: "physics",
    name: "Physics",
    description:
      "Citation styles for physics, astrophysics, and applied physics research.",
    commonStyles: ["aps", "aip", "nature"],
    relatedFields: ["mathematics", "engineering", "astronomy"],
  },
  {
    slug: "engineering",
    name: "Engineering",
    description:
      "Citation styles for engineering and applied technology research.",
    commonStyles: ["ieee", "apa", "springer"],
    relatedFields: ["computer-science", "physics", "mathematics"],
  },
  {
    slug: "computer-science",
    name: "Computer Science",
    description:
      "Citation styles for computer science, AI, and software engineering research.",
    commonStyles: ["ieee", "acm", "springer"],
    relatedFields: ["engineering", "mathematics", "information-science"],
  },
  {
    slug: "mathematics",
    name: "Mathematics & Statistics",
    description:
      "Citation styles for mathematics, applied math, and statistics research.",
    commonStyles: ["apa", "springer", "aps"],
    relatedFields: ["physics", "computer-science", "statistics"],
  },
  {
    slug: "environmental-science",
    name: "Environmental Science",
    description:
      "Citation styles for environmental research and sustainability studies.",
    commonStyles: ["cse", "apa", "nature"],
    relatedFields: ["biology", "ecology", "earth-science"],
  },
  {
    slug: "earth-science",
    name: "Earth & Geological Sciences",
    description:
      "Citation styles for geology, geophysics, and earth science research.",
    commonStyles: ["cse", "apa"],
    relatedFields: ["environmental-science", "physics", "chemistry"],
  },
  {
    slug: "art-history",
    name: "Art History & Visual Arts",
    description:
      "Citation styles for art history, visual culture, and museum studies.",
    commonStyles: ["chicago", "mla"],
    relatedFields: ["history", "literature", "architecture"],
  },
  {
    slug: "music",
    name: "Music & Musicology",
    description:
      "Citation styles for music research, theory, and performance studies.",
    commonStyles: ["chicago", "mla"],
    relatedFields: ["art-history", "history"],
  },
  {
    slug: "theology",
    name: "Theology & Religious Studies",
    description: "Citation styles for theological and religious scholarship.",
    commonStyles: ["turabian", "chicago", "sbl"],
    relatedFields: ["philosophy", "history"],
  },
  {
    slug: "social-work",
    name: "Social Work",
    description: "Citation styles for social work research and practice.",
    commonStyles: ["apa"],
    relatedFields: ["psychology", "sociology", "public-health"],
  },
  {
    slug: "linguistics",
    name: "Linguistics",
    description:
      "Citation styles for linguistics, phonetics, and language science.",
    commonStyles: ["mla", "apa", "chicago"],
    relatedFields: ["literature", "psychology", "anthropology"],
  },
  {
    slug: "architecture",
    name: "Architecture & Urban Planning",
    description:
      "Citation styles for architecture, urban design, and planning research.",
    commonStyles: ["chicago", "apa"],
    relatedFields: ["art-history", "engineering", "environmental-science"],
  },
  {
    slug: "film-media",
    name: "Film & Media Studies",
    description:
      "Citation styles for film, television, media, and communication studies.",
    commonStyles: ["mla", "chicago", "apa"],
    relatedFields: ["literature", "art-history", "journalism"],
  },
  {
    slug: "journalism",
    name: "Journalism & Communication",
    description:
      "Citation styles for journalism, media studies, and communication research.",
    commonStyles: ["apa", "chicago", "mla"],
    relatedFields: ["film-media", "political-science"],
  },
  {
    slug: "criminology",
    name: "Criminology & Criminal Justice",
    description:
      "Citation styles for criminology and criminal justice research.",
    commonStyles: ["apa", "asa"],
    relatedFields: ["law", "sociology", "psychology"],
  },
];

export function getAcademicField(slug: string): AcademicField | undefined {
  return ACADEMIC_FIELDS.find((f) => f.slug === slug);
}

// ---------------------------------------------------------------------------
// Per-style content pages (examples, format, in-text, annotated-bib, reference-page)
// ---------------------------------------------------------------------------

export interface DetailedStyleData {
  slug: string;
  name: string;
  fullName: string;
  edition: string;
  referenceListName: string;
  paperFormat: {
    font: string;
    fontSize: string;
    margins: string;
    spacing: string;
    titlePage: string;
    headers: string;
  };
  inTextRules: {
    singleAuthor: string;
    twoAuthors: string;
    threeOrMore: string;
    directQuote: string;
    noAuthor: string;
    noDate: string;
  };
  examples: {
    sourceType: string;
    referenceEntry: string;
    inTextCitation: string;
  }[];
  annotatedBibRules: string[];
  referencePageRules: string[];
}

export const DETAILED_STYLES: DetailedStyleData[] = [
  {
    slug: "apa",
    name: "APA",
    fullName: "APA 7th Edition",
    edition: "7th edition",
    referenceListName: "Reference List",
    paperFormat: {
      font: "12 pt Times New Roman, 11 pt Arial, 11 pt Calibri, or 10 pt Lucida Sans Unicode",
      fontSize: "12 pt (Times New Roman) or 11 pt (Arial/Calibri)",
      margins: "1 inch (2.54 cm) on all sides",
      spacing: "Double-spaced throughout, including the reference list",
      titlePage:
        "Separate title page with paper title, author name, institutional affiliation, course info, instructor name, and due date",
      headers:
        "Running head in uppercase on every page; page numbers top-right",
    },
    inTextRules: {
      singleAuthor: "(Smith, 2024)",
      twoAuthors: "(Smith & Jones, 2024)",
      threeOrMore: "(Smith et al., 2024)",
      directQuote: "(Smith, 2024, p. 15) or (Smith, 2024, paras. 3–4)",
      noAuthor:
        '("Article Title," 2024) — use first few words of the title in quotation marks',
      noDate: "(Smith, n.d.)",
    },
    examples: [
      {
        sourceType: "Book",
        referenceEntry:
          "Smith, J. D. (2024). The art of research. Academic Press.",
        inTextCitation: "(Smith, 2024)",
      },
      {
        sourceType: "Journal Article",
        referenceEntry:
          "Lee, A. B., & Park, C. (2024). Modern citation practices. Journal of Academic Writing, 15(2), 45–67. https://doi.org/10.1234/jaw.2024.001",
        inTextCitation: "(Lee & Park, 2024)",
      },
      {
        sourceType: "Website",
        referenceEntry:
          "World Health Organization. (2024, March 15). Global health statistics. https://www.who.int/statistics",
        inTextCitation: "(World Health Organization, 2024)",
      },
      {
        sourceType: "YouTube Video",
        referenceEntry:
          "Khan Academy. (2024, January 10). Introduction to citations [Video]. YouTube. https://youtube.com/watch?v=example",
        inTextCitation: "(Khan Academy, 2024)",
      },
      {
        sourceType: "Podcast",
        referenceEntry:
          "Garcia, M. (Host). (2024, February 5). Research methods today (No. 42) [Audio podcast episode]. In Academic Hour. Spotify.",
        inTextCitation: "(Garcia, 2024)",
      },
    ],
    annotatedBibRules: [
      "Each entry begins with a standard APA reference, followed by the annotation",
      "Annotations are typically 100–200 words and appear directly below the reference entry",
      "Use a hanging indent for the reference; the annotation starts as a new indented paragraph",
      "Annotations may be descriptive (summarize), evaluative (assess quality), or both",
      "Double-space the entire annotated bibliography",
    ],
    referencePageRules: [
      'Title the page "References" (centered, bold) at the top',
      "Start on a new page after the body of the paper",
      "Use a hanging indent (0.5 inches) for each entry",
      "Double-space all entries with no extra space between entries",
      "Alphabetize by the first author's last name",
      "Include DOIs as hyperlinks when available",
      "Italicize titles of books, journals, and volumes",
    ],
  },
  {
    slug: "mla",
    name: "MLA",
    fullName: "MLA 9th Edition",
    edition: "9th edition",
    referenceListName: "Works Cited",
    paperFormat: {
      font: "12 pt Times New Roman (or another legible font)",
      fontSize: "12 pt",
      margins: "1 inch on all sides",
      spacing: "Double-spaced throughout, including the Works Cited page",
      titlePage:
        "No separate title page required — header block on the first page with name, instructor, course, and date",
      headers:
        "Last name and page number in the upper-right corner of every page",
    },
    inTextRules: {
      singleAuthor: "(Smith 15)",
      twoAuthors: "(Smith and Jones 15)",
      threeOrMore: "(Smith et al. 15)",
      directQuote:
        "(Smith 15) — include the page number for both direct quotes and paraphrases when available",
      noAuthor:
        '("Article Title" 15) — use a shortened title in quotation marks',
      noDate:
        "MLA does not use dates in in-text citations; use (Author Page) format",
    },
    examples: [
      {
        sourceType: "Book",
        referenceEntry:
          "Smith, Jane D. The Art of Research. Academic Press, 2024.",
        inTextCitation: "(Smith 42)",
      },
      {
        sourceType: "Journal Article",
        referenceEntry:
          'Lee, Alex B., and Chris Park. "Modern Citation Practices." Journal of Academic Writing, vol. 15, no. 2, 2024, pp. 45–67.',
        inTextCitation: "(Lee and Park 50)",
      },
      {
        sourceType: "Website",
        referenceEntry:
          '"Global Health Statistics." World Health Organization, 15 Mar. 2024, www.who.int/statistics.',
        inTextCitation: '("Global Health Statistics")',
      },
      {
        sourceType: "YouTube Video",
        referenceEntry:
          '"Introduction to Citations." YouTube, uploaded by Khan Academy, 10 Jan. 2024, youtube.com/watch?v=example.',
        inTextCitation: '("Introduction to Citations")',
      },
      {
        sourceType: "Podcast",
        referenceEntry:
          'Garcia, Maria. "Research Methods Today." Academic Hour, Spotify, 5 Feb. 2024.',
        inTextCitation: "(Garcia)",
      },
    ],
    annotatedBibRules: [
      "Each entry begins with a standard MLA Works Cited entry, followed by the annotation",
      "Annotations are typically 100–200 words and appear as an indented paragraph below the citation",
      "Use a hanging indent for the citation; indent the annotation one additional level",
      "Annotations can summarize, evaluate, or reflect on the source",
      "Double-space the entire annotated bibliography",
    ],
    referencePageRules: [
      'Title the page "Works Cited" (centered) at the top — do not bold, italicize, or underline',
      "Start on a new page after the body of the paper",
      "Use a hanging indent (0.5 inches) for each entry",
      "Double-space all entries",
      "Alphabetize by the first element in each entry (usually the author's last name)",
      "Italicize titles of books, journals, and websites",
      "Use quotation marks around titles of articles, chapters, and short works",
    ],
  },
  {
    slug: "chicago",
    name: "Chicago",
    fullName: "Chicago Manual of Style 18th Edition",
    edition: "18th edition",
    referenceListName: "Bibliography",
    paperFormat: {
      font: "12 pt Times New Roman or similar serif font",
      fontSize: "12 pt",
      margins: "1 inch on all sides",
      spacing:
        "Double-spaced for text; single-spaced for block quotes, notes, and bibliography entries (with a blank line between entries)",
      titlePage:
        "Separate title page with title centered one-third from the top, author name, class info, and date centered lower",
      headers:
        "Page numbers top-right or bottom-center; no running head required",
    },
    inTextRules: {
      singleAuthor:
        "(Smith 2024, 15) or footnote: Smith, The Art of Research, 15.",
      twoAuthors: "(Smith and Jones 2024, 15)",
      threeOrMore: "(Smith et al. 2024, 15)",
      directQuote:
        "(Smith 2024, 15) — always include page numbers for direct quotes",
      noAuthor: 'Use a shortened title: ("Art of Research" 2024)',
      noDate: "(Smith n.d.)",
    },
    examples: [
      {
        sourceType: "Book",
        referenceEntry:
          "Smith, Jane D. The Art of Research. Academic Press, 2024.",
        inTextCitation: "(Smith 2024, 42)",
      },
      {
        sourceType: "Journal Article",
        referenceEntry:
          'Lee, Alex B., and Chris Park. "Modern Citation Practices." Journal of Academic Writing 15, no. 2 (2024): 45–67.',
        inTextCitation: "(Lee and Park 2024, 50)",
      },
      {
        sourceType: "Website",
        referenceEntry:
          'World Health Organization. "Global Health Statistics." Accessed March 15, 2024. https://www.who.int/statistics.',
        inTextCitation: "(World Health Organization 2024)",
      },
      {
        sourceType: "YouTube Video",
        referenceEntry:
          'Khan Academy. "Introduction to Citations." January 10, 2024. YouTube video. https://youtube.com/watch?v=example.',
        inTextCitation: "(Khan Academy 2024)",
      },
      {
        sourceType: "Podcast",
        referenceEntry:
          'Garcia, Maria. "Research Methods Today." Academic Hour. Podcast audio. February 5, 2024.',
        inTextCitation: "(Garcia 2024)",
      },
    ],
    annotatedBibRules: [
      "Each entry begins with a standard Chicago bibliography entry, followed by the annotation",
      "Annotations typically run 100–200 words and appear as a new paragraph below the entry",
      "Use a hanging indent for the bibliography entry",
      "The annotation paragraph is not indented further than the regular entry text",
      "Single-space within annotations; leave a blank line between entries",
    ],
    referencePageRules: [
      'Title the page "Bibliography" (centered) at the top',
      "Start on a new page",
      "Use a hanging indent for each entry",
      "Single-space within entries; add a blank line between entries",
      "Alphabetize by the first author's last name",
      "Invert the first author's name (Last, First); subsequent authors in normal order",
      "Italicize titles of books, journals, and websites",
    ],
  },
  {
    slug: "harvard",
    name: "Harvard",
    fullName: "Harvard Referencing (Cite Them Right)",
    edition: "Cite Them Right 11th edition",
    referenceListName: "Reference List",
    paperFormat: {
      font: "12 pt Arial, Calibri, or Times New Roman",
      fontSize: "12 pt",
      margins:
        "1 inch (2.54 cm) on all sides, or as specified by your institution",
      spacing: "Double-spaced or 1.5 spacing as required by your institution",
      titlePage: "Varies by institution — check your department's requirements",
      headers: "Page numbers as required; no universal running head standard",
    },
    inTextRules: {
      singleAuthor: "(Smith, 2024)",
      twoAuthors: "(Smith and Jones, 2024)",
      threeOrMore: "(Smith et al., 2024)",
      directQuote:
        "(Smith, 2024, p. 15) — include the page number for direct quotes",
      noAuthor: "(Organisation Name, 2024) or (Title of work, 2024)",
      noDate: "(Smith, no date)",
    },
    examples: [
      {
        sourceType: "Book",
        referenceEntry:
          "Smith, J.D. (2024) The art of research. London: Academic Press.",
        inTextCitation: "(Smith, 2024)",
      },
      {
        sourceType: "Journal Article",
        referenceEntry:
          "Lee, A.B. and Park, C. (2024) 'Modern citation practices', Journal of Academic Writing, 15(2), pp. 45–67.",
        inTextCitation: "(Lee and Park, 2024)",
      },
      {
        sourceType: "Website",
        referenceEntry:
          "World Health Organization (2024) Global health statistics. Available at: https://www.who.int/statistics (Accessed: 15 March 2024).",
        inTextCitation: "(World Health Organization, 2024)",
      },
      {
        sourceType: "YouTube Video",
        referenceEntry:
          "Khan Academy (2024) Introduction to citations. Available at: https://youtube.com/watch?v=example (Accessed: 20 March 2024).",
        inTextCitation: "(Khan Academy, 2024)",
      },
      {
        sourceType: "Podcast",
        referenceEntry:
          "Garcia, M. (2024) 'Research methods today', Academic Hour [Podcast]. 5 February. Available at: Spotify.",
        inTextCitation: "(Garcia, 2024)",
      },
    ],
    annotatedBibRules: [
      "Begin with a standard Harvard reference list entry",
      "Follow with a 100–200 word annotation summarizing and/or evaluating the source",
      "Use a hanging indent for the reference entry",
      "The annotation follows as an indented paragraph",
      "Maintain consistent spacing throughout",
    ],
    referencePageRules: [
      'Title the page "Reference List" or "References" (centered or left-aligned per your institution)',
      "Start on a new page",
      "Use a hanging indent for each entry",
      "Alphabetize by the first author's surname",
      "Use single spacing within entries with a blank line between entries (or double-space throughout — check your institution's guide)",
      "Italicize titles of books and journals",
      "Use single quotation marks around article and chapter titles",
    ],
  },
  {
    slug: "vancouver",
    name: "Vancouver",
    fullName: "Vancouver (ICMJE Recommendations)",
    edition: "ICMJE standard",
    referenceListName: "References",
    paperFormat: {
      font: "12 pt Times New Roman or Arial",
      fontSize: "12 pt",
      margins: "1 inch on all sides",
      spacing: "Double-spaced for the manuscript body",
      titlePage:
        "Separate title page with article title, author names, affiliations, and corresponding author details",
      headers: "Page numbers in upper-right corner",
    },
    inTextRules: {
      singleAuthor: "(1) or superscript ¹ — numbered in order of first mention",
      twoAuthors: "Same number as the original reference, e.g. (1)",
      threeOrMore:
        "Same number — Vancouver uses a single number per source regardless of author count",
      directQuote:
        "Use the same numbered reference; add page number in the text if needed",
      noAuthor:
        "Same numbered system — the reference number is assigned in order of appearance",
      noDate: "Include [date unknown] in the reference list entry",
    },
    examples: [
      {
        sourceType: "Book",
        referenceEntry:
          "1. Smith JD. The art of research. London: Academic Press; 2024.",
        inTextCitation: "(1)",
      },
      {
        sourceType: "Journal Article",
        referenceEntry:
          "2. Lee AB, Park C. Modern citation practices. J Acad Writ. 2024;15(2):45-67.",
        inTextCitation: "(2)",
      },
      {
        sourceType: "Website",
        referenceEntry:
          "3. World Health Organization. Global health statistics [Internet]. Geneva: WHO; 2024 [cited 2024 Mar 15]. Available from: https://www.who.int/statistics",
        inTextCitation: "(3)",
      },
      {
        sourceType: "YouTube Video",
        referenceEntry:
          "4. Khan Academy. Introduction to citations [Video]. YouTube; 2024 Jan 10 [cited 2024 Mar 20]. Available from: https://youtube.com/watch?v=example",
        inTextCitation: "(4)",
      },
      {
        sourceType: "Podcast",
        referenceEntry:
          "5. Garcia M. Research methods today. In: Academic Hour [Podcast]. 2024 Feb 5.",
        inTextCitation: "(5)",
      },
    ],
    annotatedBibRules: [
      "Annotated bibliographies are less common in Vancouver style but follow the same numbered format",
      "Each entry begins with the numbered reference, followed by the annotation",
      "Annotations typically run 100–200 words and appear below the reference",
      "Maintain the numbered order as it appears in the text",
      "Double-space the annotations",
    ],
    referencePageRules: [
      'Title the page "References" at the top',
      "Start on a new page after the body text",
      "Number references consecutively in the order they first appear in the text",
      "Use abbreviated journal names (per PubMed/MEDLINE standards)",
      "List up to 6 authors, then use et al.",
      "Do NOT use hanging indents — numbers are left-aligned",
      "Include DOIs or URLs when available",
    ],
  },
  {
    slug: "ieee",
    name: "IEEE",
    fullName: "IEEE Citation Style",
    edition: "standard",
    referenceListName: "References",
    paperFormat: {
      font: "10 pt Times New Roman (for IEEE conference papers) or 12 pt for general use",
      fontSize: "10 pt for conference papers; 12 pt for theses",
      margins:
        "IEEE conference template margins (varies); standard 1-inch for theses",
      spacing: "Single-spaced for conference papers; double-spaced for theses",
      titlePage:
        "Conference papers use IEEE template headers; theses follow institutional guidelines",
      headers:
        "IEEE papers use two-column format with specific headers per template",
    },
    inTextRules: {
      singleAuthor: "[1] — numbered in square brackets in order of appearance",
      twoAuthors: "[1] — same number for the same reference",
      threeOrMore: "[1] — numbered system does not change with author count",
      directQuote:
        "[1, p. 15] — include the page number after the reference number",
      noAuthor: "[1] — numbered in order of first mention regardless",
      noDate: "[1] — include available date information in the reference list",
    },
    examples: [
      {
        sourceType: "Book",
        referenceEntry:
          "[1] J. D. Smith, The Art of Research. London, UK: Academic Press, 2024.",
        inTextCitation: "[1]",
      },
      {
        sourceType: "Journal Article",
        referenceEntry:
          '[2] A. B. Lee and C. Park, "Modern citation practices," J. Acad. Writ., vol. 15, no. 2, pp. 45–67, 2024.',
        inTextCitation: "[2]",
      },
      {
        sourceType: "Website",
        referenceEntry:
          '[3] World Health Organization. "Global Health Statistics." WHO. https://www.who.int/statistics (accessed Mar. 15, 2024).',
        inTextCitation: "[3]",
      },
      {
        sourceType: "Conference Paper",
        referenceEntry:
          '[4] J. D. Smith, "New approaches to citation," in Proc. IEEE Int. Conf. Acad. Writing, New York, NY, USA, 2024, pp. 100–105.',
        inTextCitation: "[4]",
      },
      {
        sourceType: "Standard",
        referenceEntry:
          "[5] IEEE Standard for Citation Format, IEEE Std 12345-2024, 2024.",
        inTextCitation: "[5]",
      },
    ],
    annotatedBibRules: [
      "Annotated bibliographies are uncommon in IEEE but follow the numbered format when used",
      "Each entry begins with the numbered IEEE reference",
      "Annotations appear below the reference as a regular paragraph",
      "Keep annotations concise (100–150 words) to match IEEE's technical writing conventions",
      "Maintain the numbered order from the paper",
    ],
    referencePageRules: [
      'Title the section "References" (left-aligned, bold)',
      "Number references in the order they appear in the text using square brackets [1], [2], etc.",
      "Use abbreviated journal names",
      "Use initials for first and middle names (J. D. Smith, not Jane D. Smith)",
      "Italicize book and journal titles",
      "Use sentence case for article titles (in quotation marks)",
      "Include DOIs or URLs for online sources",
    ],
  },
  {
    slug: "ama",
    name: "AMA",
    fullName: "AMA 11th Edition",
    edition: "11th edition",
    referenceListName: "References",
    paperFormat: {
      font: "12 pt Times New Roman or Arial",
      fontSize: "12 pt",
      margins: "1 inch on all sides",
      spacing: "Double-spaced throughout",
      titlePage:
        "Separate title page with title, authors, affiliations, and corresponding author contact information",
      headers: "Page numbers in the upper-right corner; running head optional",
    },
    inTextRules: {
      singleAuthor:
        "Superscript number after punctuation¹ — numbered in order of first mention",
      twoAuthors: "Same superscript number — e.g., Smith and Jones¹",
      threeOrMore:
        "List up to 6 authors; 7 or more use first 3 et al — referenced by the same number",
      directQuote:
        "Superscript number placed after the quotation mark¹; page numbers cited in text if needed",
      noAuthor:
        "Numbered in order of appearance; use institutional or organizational name in reference list",
      noDate:
        "Include [published year unknown] or omit year with a note in the reference entry",
    },
    examples: [
      {
        sourceType: "Book",
        referenceEntry:
          "1. Smith JD. The Art of Research. Academic Press; 2024.",
        inTextCitation: "¹",
      },
      {
        sourceType: "Journal Article",
        referenceEntry:
          "2. Lee AB, Park C. Modern citation practices. J Acad Writ. 2024;15(2):45-67. doi:10.1234/jaw.2024.001",
        inTextCitation: "²",
      },
      {
        sourceType: "Website",
        referenceEntry:
          "3. World Health Organization. Global health statistics. Published March 15, 2024. Accessed March 20, 2024. https://www.who.int/statistics",
        inTextCitation: "³",
      },
      {
        sourceType: "Conference Paper",
        referenceEntry:
          "4. Smith JD, Jones AM. New approaches to medical citation. In: Proceedings of the Annual Medical Writers Conference; 2024; Boston, MA. Medical Press; 2024:100-105.",
        inTextCitation: "⁴",
      },
      {
        sourceType: "Government Report",
        referenceEntry:
          "5. Centers for Disease Control and Prevention. Annual surveillance report of drug-related risks. US Department of Health and Human Services; 2024.",
        inTextCitation: "⁵",
      },
    ],
    annotatedBibRules: [
      "Annotated bibliographies are not standard in AMA but follow the numbered reference format when used",
      "Each numbered entry is followed by a brief annotation of 100–150 words",
      "Annotations should describe the study design, findings, and relevance to your topic",
      "Maintain the numerical order as references appear in the text",
      "Use abbreviated journal names (JAMA, not Journal of the American Medical Association)",
    ],
    referencePageRules: [
      'Title the page "References" at the top',
      "Number references consecutively in the order they first appear in the text",
      "Use superscript numerals in the text; list full references at the end",
      "Abbreviate journal names using standard medical abbreviations (e.g., N Engl J Med)",
      "List up to 6 authors; for 7 or more, list the first 3 followed by et al",
      "Include DOIs for all journal articles when available",
      "Do not use hanging indents — numbers are left-aligned followed by a period",
    ],
  },
  {
    slug: "turabian",
    name: "Turabian",
    fullName: "Turabian 9th Edition",
    edition: "9th edition",
    referenceListName: "Bibliography",
    paperFormat: {
      font: "12 pt Times New Roman or similar serif font",
      fontSize: "12 pt",
      margins:
        "1 inch on all sides (left margin may be 1.25 inches for bound papers)",
      spacing:
        "Double-spaced for body text; single-spaced for block quotes and bibliography entries with a blank line between entries",
      titlePage:
        "Separate title page with title centered one-third from the top, author name, course info, instructor, and date centered in the lower half",
      headers:
        "Page numbers in the upper-right corner; no running head required",
    },
    inTextRules: {
      singleAuthor:
        "Notes-Bibliography: footnote — Smith, The Art of Research, 15. | Author-Date: (Smith 2024, 15)",
      twoAuthors:
        "Notes-Bibliography: Smith and Jones, Title, 15. | Author-Date: (Smith and Jones 2024, 15)",
      threeOrMore:
        "Notes-Bibliography: Smith et al., Title, 15. | Author-Date: (Smith et al. 2024, 15)",
      directQuote:
        "Always include page numbers: footnote — Smith, Title, 15 | Author-Date — (Smith 2024, 15)",
      noAuthor:
        'Shorten the title: footnote — "Art of Research," 15 | Author-Date — ("Art of Research" 2024)',
      noDate: "Notes-Bibliography: Smith, n.d. | Author-Date: (Smith n.d.)",
    },
    examples: [
      {
        sourceType: "Book",
        referenceEntry:
          "Smith, Jane D. The Art of Research. Chicago: Academic Press, 2024.",
        inTextCitation:
          "(Smith 2024, 42) or footnote: Smith, The Art of Research, 42.",
      },
      {
        sourceType: "Journal Article",
        referenceEntry:
          'Lee, Alex B., and Chris Park. "Modern Citation Practices." Journal of Academic Writing 15, no. 2 (2024): 45–67.',
        inTextCitation:
          '(Lee and Park 2024, 50) or footnote: Lee and Park, "Modern Citation Practices," 50.',
      },
      {
        sourceType: "Website",
        referenceEntry:
          'World Health Organization. "Global Health Statistics." Accessed March 15, 2024. https://www.who.int/statistics.',
        inTextCitation:
          '(World Health Organization 2024) or footnote: World Health Organization, "Global Health Statistics."',
      },
      {
        sourceType: "Book Chapter",
        referenceEntry:
          'Jones, Maria. "Methods in Research." In Handbook of Academic Writing, edited by Sarah Brown, 55–80. New York: University Press, 2024.',
        inTextCitation:
          '(Jones 2024, 60) or footnote: Jones, "Methods in Research," 60.',
      },
      {
        sourceType: "Thesis",
        referenceEntry:
          'Garcia, Carlos. "Citation Practices in the Digital Age." PhD diss., University of Chicago, 2024.',
        inTextCitation:
          '(Garcia 2024, 33) or footnote: Garcia, "Citation Practices," 33.',
      },
    ],
    annotatedBibRules: [
      "Each entry begins with a standard Turabian bibliography entry, followed by the annotation",
      "Annotations are typically 100–200 words and appear as a new paragraph below the entry",
      "Use a hanging indent for the bibliography entry; the annotation paragraph aligns with the text of the entry",
      "Single-space within annotations; leave a blank line between annotated entries",
      "Annotations may be descriptive, evaluative, or a combination of both",
    ],
    referencePageRules: [
      'Title the page "Bibliography" (centered) at the top for Notes-Bibliography system, or "References" for Author-Date',
      "Start on a new page",
      "Use a hanging indent for each entry",
      "Single-space within entries; add a blank line between entries",
      "Alphabetize by the first author's last name",
      "Invert the first author's name (Last, First); subsequent authors in normal order",
      "Italicize titles of books, journals, and other long works",
    ],
  },
  {
    slug: "cse",
    name: "CSE",
    fullName: "CSE 8th Edition",
    edition: "8th edition",
    referenceListName: "References",
    paperFormat: {
      font: "12 pt Times New Roman or Arial",
      fontSize: "12 pt",
      margins: "1 inch on all sides",
      spacing: "Double-spaced for body text",
      titlePage:
        "Separate title page with title, author names, institutional affiliation, running head, and word count",
      headers:
        "Running head (shortened title) in the upper-left; page numbers in the upper-right",
    },
    inTextRules: {
      singleAuthor:
        "Citation-Sequence: superscript¹ | Citation-Name: superscript¹ | Name-Year: (Smith 2024)",
      twoAuthors:
        "Citation-Sequence/Name: same superscript | Name-Year: (Smith and Jones 2024)",
      threeOrMore:
        "Citation-Sequence/Name: same superscript | Name-Year: (Smith et al. 2024) — for 3+ authors",
      directQuote:
        "Use the same numbered reference or (Author Year, p. X) in Name-Year system",
      noAuthor:
        "Use the organization name or a shortened title in Name-Year: ([Anonymous] 2024)",
      noDate: "Name-Year system: (Smith [date unknown]) or (Smith [ca. 2020])",
    },
    examples: [
      {
        sourceType: "Book",
        referenceEntry:
          "1. Smith JD. The art of research. London (UK): Academic Press; 2024. 300 p.",
        inTextCitation: "¹ or (Smith 2024)",
      },
      {
        sourceType: "Journal Article",
        referenceEntry:
          "2. Lee AB, Park C. Modern citation practices. J Acad Writ. 2024;15(2):45–67.",
        inTextCitation: "² or (Lee and Park 2024)",
      },
      {
        sourceType: "Website",
        referenceEntry:
          "3. World Health Organization. Global health statistics [Internet]. Geneva (CH): WHO; 2024 [cited 2024 Mar 15]. Available from: https://www.who.int/statistics",
        inTextCitation: "³ or (World Health Organization 2024)",
      },
      {
        sourceType: "Conference Paper",
        referenceEntry:
          "4. Smith JD. Citation in biology: new standards. In: Brown S, editor. Proceedings of the International Science Writers Conference; 2024 Jun 10–12; Boston, MA. London (UK): Academic Press; 2024. p. 88–95.",
        inTextCitation: "⁴ or (Smith 2024)",
      },
      {
        sourceType: "Book Chapter",
        referenceEntry:
          "5. Jones MA. Ecological citation methods. In: Green P, editor. Handbook of environmental science. Oxford (UK): Oxford University Press; 2024. p. 101–125.",
        inTextCitation: "⁵ or (Jones 2024)",
      },
    ],
    annotatedBibRules: [
      "CSE style offers three citation systems: Citation-Sequence, Citation-Name, and Name-Year",
      "In Citation-Sequence and Citation-Name, references are numbered; annotations follow below each numbered entry",
      "In Name-Year, references are alphabetical; annotations follow below each alphabetical entry",
      "Annotations are typically 50–150 words and describe the study design, methods, and relevance",
      "Maintain the chosen system consistently throughout the annotated bibliography",
      "Use abbreviated journal names per CSE/BIOSIS standards",
    ],
    referencePageRules: [
      'Title the page "References" (Citation-Sequence/Citation-Name) or "References" (Name-Year)',
      "Citation-Sequence: number in order of first appearance in text",
      "Citation-Name: number alphabetically by first author's last name",
      "Name-Year: list alphabetically without numbers",
      "Abbreviate journal names (no periods in abbreviations)",
      "List all authors — do not use et al. in the reference list (only in-text for Name-Year with 3+ authors)",
      "Include location of publisher and total page count for books",
    ],
  },
  {
    slug: "nature",
    name: "Nature",
    fullName: "Nature Journal Style",
    edition: "current",
    referenceListName: "References",
    paperFormat: {
      font: "12 pt Arial or Times New Roman",
      fontSize: "12 pt",
      margins: "1 inch on all sides",
      spacing: "Double-spaced for manuscripts submitted to Nature journals",
      titlePage:
        "Title, authors, affiliations, abstract (150 words max), and key words on the first page; no separate title page",
      headers:
        "Page numbers bottom-center; author name and abbreviated title in header for some journals",
    },
    inTextRules: {
      singleAuthor:
        "Superscript number after punctuation¹ — numbered in order of first mention",
      twoAuthors:
        "Same superscript number — both authors cited in the reference list",
      threeOrMore:
        "Same superscript number — all authors listed in the reference list (no et al. in reference list)",
      directQuote:
        "Superscript number¹; page number cited in the text if applicable",
      noAuthor:
        "Use the organization or dataset name; numbered in order of first mention",
      noDate:
        "Include the year if available; for preprints use the posted year",
    },
    examples: [
      {
        sourceType: "Journal Article",
        referenceEntry:
          "Lee, A. B. & Park, C. Modern citation practices. J. Acad. Writ. 15, 45–67 (2024).",
        inTextCitation: "¹",
      },
      {
        sourceType: "Book",
        referenceEntry:
          "Smith, J. D. The Art of Research (Academic Press, 2024).",
        inTextCitation: "²",
      },
      {
        sourceType: "Website",
        referenceEntry:
          "World Health Organization. Global health statistics. WHO https://www.who.int/statistics (2024).",
        inTextCitation: "³",
      },
      {
        sourceType: "Conference Paper",
        referenceEntry:
          "Smith, J. D. New approaches to citation. In Proc. Int. Conf. Acad. Writing 100–105 (Academic Press, 2024).",
        inTextCitation: "⁴",
      },
      {
        sourceType: "Preprint",
        referenceEntry:
          "Garcia, M. Citation trends in open science. Preprint at https://doi.org/10.1101/example (2024).",
        inTextCitation: "⁵",
      },
    ],
    annotatedBibRules: [
      "Annotated bibliographies are not standard in Nature style but may be used in review manuscripts",
      "References are numbered in order of appearance; annotations follow each numbered entry",
      "Annotations are brief (50–100 words) and focus on study design, key findings, and limitations",
      "No article titles are included for journal references in the standard reference list",
      "Use abbreviated journal names (italicized) following Nature's approved abbreviation list",
    ],
    referencePageRules: [
      'Title the section "References" at the end of the manuscript',
      "Number references consecutively in the order they appear in the text",
      "For journals: Author, A. B. & Author, C. D. J. Abbrev. Name vol, pages (year).",
      "Do NOT include article titles for journal references",
      "Abbreviate journal names in italics",
      "Use '&' between authors — not 'and'",
      "List all authors — do not use et al. in the reference list",
    ],
  },
  {
    slug: "acs",
    name: "ACS",
    fullName: "ACS Guide to Scholarly Communication",
    edition: "current",
    referenceListName: "References",
    paperFormat: {
      font: "12 pt Times New Roman",
      fontSize: "12 pt",
      margins: "1 inch on all sides",
      spacing: "Double-spaced for manuscripts",
      titlePage:
        "Title, author names, affiliations, corresponding author email, and abstract on the first page",
      headers:
        "Page numbers in the upper-right; running head (abbreviated title) in upper-left",
    },
    inTextRules: {
      singleAuthor:
        "Superscript italic number after punctuation¹ (order-of-citation) or (Smith, 2024) for author-date",
      twoAuthors: "Same superscript number or (Smith and Jones, 2024)",
      threeOrMore:
        "Same superscript number or (Smith et al., 2024) for 3+ authors",
      directQuote:
        "Superscript number or (Smith, 2024, p 15) — page numbers cited when needed",
      noAuthor:
        "Use organization name or abbreviated title; numbered in order of appearance",
      noDate:
        "(Smith, year unknown) or include the best available date estimate",
    },
    examples: [
      {
        sourceType: "Journal Article",
        referenceEntry:
          "(1) Lee, A. B.; Park, C. Modern Citation Practices. J. Acad. Writ. 2024, 15 (2), 45–67. DOI: 10.1234/jaw.2024.001",
        inTextCitation: "¹",
      },
      {
        sourceType: "Book",
        referenceEntry:
          "(2) Smith, J. D. The Art of Research; Academic Press, 2024.",
        inTextCitation: "²",
      },
      {
        sourceType: "Website",
        referenceEntry:
          "(3) World Health Organization. Global Health Statistics. https://www.who.int/statistics (accessed 2024-03-15)",
        inTextCitation: "³",
      },
      {
        sourceType: "Book Chapter",
        referenceEntry:
          "(4) Jones, M. A. Methods in Chemistry Research. In Handbook of Chemical Methods; Brown, S., Ed.; Academic Press, 2024; pp 55–80.",
        inTextCitation: "⁴",
      },
      {
        sourceType: "Patent",
        referenceEntry:
          "(5) Garcia, C. Citation Management System. US12345678B2, 2024.",
        inTextCitation: "⁵",
      },
    ],
    annotatedBibRules: [
      "Annotated bibliographies are not standard in ACS style but may be adapted from numbered reference lists",
      "Each numbered entry is followed by a brief annotation of 75–150 words",
      "Annotations focus on experimental methods, key findings, and relevance to the research topic",
      "Journal names may be abbreviated per the Chemical Abstracts Service (CAS) Source Index or written in full; pick one form and use it for every journal",
      "Use semicolons to separate multiple authors (Lee, A. B.; Park, C.)",
    ],
    referencePageRules: [
      'Title the section "References" at the end of the manuscript',
      "Number references in the order they appear in the text; place the number in parentheses at the start of each entry",
      "Separate multiple authors with semicolons",
      "Abbreviating journal names per CAS is optional and full journal titles are allowed; keep one form throughout the list",
      "Include the DOI for all journal articles whenever available",
      "For books, give the publisher and year; add the location only for a lesser-known or ambiguous publisher, as Location: Publisher, Year",
      "Italicize journal names and book titles",
    ],
  },
  {
    slug: "oscola",
    name: "OSCOLA",
    fullName:
      "Oxford University Standard for the Citation of Legal Authorities 5th Edition",
    edition: "5th edition",
    referenceListName: "Bibliography",
    paperFormat: {
      font: "12 pt Times New Roman",
      fontSize: "12 pt",
      margins: "1 inch on all sides",
      spacing:
        "Double-spaced for body text; single-spaced for footnotes and bibliography",
      titlePage:
        "Title, author name, module/course, tutor name, and submission date — format varies by institution",
      headers:
        "Page numbers in the upper-right or bottom-center; footnote numbers are superscript in body text",
    },
    inTextRules: {
      singleAuthor:
        "Footnote number in superscript after punctuation — ¹ Smith v Jones [2024] UKSC 1 [15].",
      twoAuthors:
        "Same footnote number style — cite both parties for cases or both authors for secondary sources",
      threeOrMore:
        "For books with more than three authors: first author followed by 'and others'; for cases cite the case name normally",
      directQuote:
        "Footnote with pinpoint reference — ¹ Smith v Jones [2024] UKSC 1 [15] (Lord Hope).",
      noAuthor:
        "Cite by document title, court, or institution — ¹ Law Commission, Report No 123 (2024) para 5.",
      noDate:
        "Include [date unknown] or use the most recent edition information available",
    },
    examples: [
      {
        sourceType: "Case",
        referenceEntry: "Smith v Jones [2024] UKSC 1.",
        inTextCitation: "¹ (footnote: Smith v Jones [2024] UKSC 1 [15].)",
      },
      {
        sourceType: "Legislation",
        referenceEntry: "Legal Citation Act 2024, s 5(1).",
        inTextCitation: "² (footnote: Legal Citation Act 2024, s 5(1).)",
      },
      {
        sourceType: "Journal Article",
        referenceEntry:
          "Alex Lee, 'Modern Citation in Law' (2024) 15 Oxford J Legal Stud 45.",
        inTextCitation: "⁹ (later footnote: Lee (n 3) 50.)",
      },
      {
        sourceType: "Book",
        referenceEntry:
          "Jane D Smith, The Art of Legal Research (Academic Press 2024).",
        inTextCitation: "¹⁰ (later footnote: Smith (n 4) 42.)",
      },
      {
        sourceType: "Website",
        referenceEntry:
          "World Health Organization, 'Global Health Statistics' (WHO, 15 March 2024) <https://www.who.int/statistics> accessed 20 March 2024.",
        inTextCitation: "¹¹ (later footnote: WHO (n 5).)",
      },
    ],
    annotatedBibRules: [
      "Annotated bibliographies are not standard in OSCOLA but are sometimes used in legal research essays",
      "In a long work, primary sources go in a table of cases and a table of legislation at the start; the bibliography lists secondary sources only",
      "Case names are italicised in footnotes but not in the table of cases",
      "The table of legislation lists statutes in alphabetical order of title, with statutory instruments after them",
      "Secondary sources (books, articles, reports) are listed alphabetically by author's surname in the Bibliography",
    ],
    referencePageRules: [
      "A long work has a table of cases and a table of legislation at the start; the bibliography at the back lists secondary sources only",
      "Table of Cases: list alphabetically by case name, in roman type, with the full citation",
      "Table of Legislation: list statutes alphabetically, then statutory instruments",
      "Bibliography: list secondary sources alphabetically by author's surname, with initials in place of forenames and no full stop at the end of an entry",
      "Do not use ibid or other Latin gadgets such as op cit and supra. The 4th edition allowed ibid; the 5th edition (2026) does not — cite the short name and the footnote number instead, as in Smith (n 4) 42",
      "In footnotes, case names are italicised and legislation is not",
      "Paragraph pinpoints go in square brackets straight after the citation, as in [15], with no 'at'; page pinpoints stand alone",
      "A case with no medium neutral citation takes a court identifier in brackets after the report: (HL), (PC), (CA), (KBD), (QBD), (Ch D) or (Fam). Cases decided before 1865 need none",
      "Changed since the 4th edition: ibid is dropped; a neutral citation is now a medium neutral citation, never invented for a pre-2001 case; High Court identifiers are KBD, QBD, Ch D and Fam; an Act's short form goes in single quotation marks; websites prefer a persistent link or DOI to an access date; and new rules cover social media, podcasts and generative AI",
      "The OSCOLA output generated on this site follows the 4th-edition CSL style, because no 5th-edition CSL style has been published in the Citation Style Language repository yet. Adjust it by hand: replace any ibid with the short name and (n X), use the 5th-edition court identifiers, put an Act's short form in single quotation marks, and prefer a persistent link or DOI to an access date",
    ],
  },
  {
    slug: "bluebook",
    name: "Bluebook",
    fullName: "The Bluebook: A Uniform System of Citation 22nd Edition",
    edition: "22nd edition",
    referenceListName: "Table of Authorities",
    paperFormat: {
      font: "12 pt Times New Roman",
      fontSize: "12 pt",
      margins: "1 inch on all sides",
      spacing:
        "Double-spaced for law review articles; single-spaced for court briefs",
      titlePage:
        "Law review articles: author, title, volume, journal, page, and year on the cover page; court documents follow local rules",
      headers:
        "Page numbers required; law review articles use running headers with author name and article title",
    },
    inTextRules: {
      singleAuthor:
        "Law review: footnote with full citation — ¹ Smith v. Jones, 123 U.S. 456, 459 (2024). | Briefs: id. or supra/infra cross-references",
      twoAuthors:
        "Same footnote format — cite both parties for cases, both authors for secondary sources",
      threeOrMore:
        "For law review articles with 3+ authors: use all names or first author et al. per convention",
      directQuote:
        "Pinpoint citation required — ¹ Smith v. Jones, 123 U.S. 456, 459 (2024) ('quoted language').",
      noAuthor:
        "Cite by document title with signal (e.g., See Memorandum from DOJ, supra note 5, at 3.)",
      noDate:
        "Include the year of publication or decision; for pending cases use the current year",
    },
    examples: [
      {
        sourceType: "Court Case",
        referenceEntry: "Smith v. Jones, 123 U.S. 456 (2024).",
        inTextCitation:
          "¹ (footnote: Smith v. Jones, 123 U.S. 456, 459 (2024).)",
      },
      {
        sourceType: "Federal Statute",
        referenceEntry:
          "Legal Citation Reform Act of 2024, Pub. L. No. 118-100, 138 Stat. 1234.",
        inTextCitation: "² (footnote: 42 U.S.C. § 1983 (2024).)",
      },
      {
        sourceType: "Law Review Article",
        referenceEntry:
          "Alex B. Lee, Modern Citation Practices, 115 Yale L.J. 45 (2024).",
        inTextCitation: "³ (footnote: Lee, supra note 3, at 50.)",
      },
      {
        sourceType: "Book",
        referenceEntry:
          "Jane D. Smith, The Art of Legal Research 42 (Academic Press 2024).",
        inTextCitation: "⁴ (footnote: Smith, supra note 4, at 42.)",
      },
      {
        sourceType: "Website",
        referenceEntry:
          "World Health Organization, Global Health Statistics, https://www.who.int/statistics (last visited Mar. 20, 2024).",
        inTextCitation: "⁵ (footnote: WHO, supra note 5.)",
      },
    ],
    annotatedBibRules: [
      "Annotated bibliographies are not standard in Bluebook but may appear in legal research memos and papers",
      "A Table of Authorities (not a bibliography) is required for court briefs — lists all cases, statutes, and secondary sources",
      "Cases, statutes, and secondary sources are each grouped separately in the Table of Authorities",
      "Cases are alphabetized by first party name; statutes are alphabetized by popular name",
      "Law review footnotes use specific signals (See, Cf., But see, Contra) to indicate the relationship to the cited proposition",
    ],
    referencePageRules: [
      "Court briefs require a Table of Authorities divided into: Cases, Statutes, and Other Authorities",
      "Law review articles list footnotes at the bottom of each page — not in a separate reference list",
      "Case citations: Party v. Party, volume Reporter page (Court Year). E.g., Smith v. Jones, 123 U.S. 456 (2024).",
      "Statute citations: Name of Act, Public Law number, Statutes at Large citation, and codified location",
      "Use signals (See, E.g., Accord, See also, Cf., But cf., But see, Contra, See generally) before citations",
      "Subsequent references use 'id.' for the immediately preceding citation or 'supra note X' for earlier footnotes",
      "Case names are italicized in law review footnotes but not in court documents",
    ],
  },
  {
    slug: "asa",
    name: "ASA",
    fullName: "ASA 7th Edition",
    edition: "7th edition",
    referenceListName: "References",
    paperFormat: {
      font: "12 pt Times New Roman",
      fontSize: "12 pt",
      margins: "1.25 inches on all sides",
      spacing: "Double-spaced throughout, including the reference list",
      titlePage:
        "Separate title page with the title centered, author name and institutional affiliation centered below, and word count in the bottom-right corner",
      headers:
        "Page numbers in the upper-right corner; no running head required",
    },
    inTextRules: {
      singleAuthor: "(Smith 2024)",
      twoAuthors: "(Smith and Jones 2024)",
      threeOrMore: "(Smith et al. 2024) for 3+ authors",
      directQuote:
        "(Smith 2024:15) — use a colon before the page number (not p. or pp.)",
      noAuthor:
        '("Article Title" 2024) — use the title in double quotation marks',
      noDate: "(Smith n.d.)",
    },
    examples: [
      {
        sourceType: "Book",
        referenceEntry:
          "Smith, Jane D. 2024. The Art of Research. London: Academic Press.",
        inTextCitation: "(Smith 2024)",
      },
      {
        sourceType: "Journal Article",
        referenceEntry:
          'Lee, Alex B. and Chris Park. 2024. "Modern Citation Practices." Journal of Academic Writing 15(2):45–67.',
        inTextCitation: "(Lee and Park 2024:50)",
      },
      {
        sourceType: "Website",
        referenceEntry:
          'World Health Organization. 2024. "Global Health Statistics." Retrieved March 20, 2024 (https://www.who.int/statistics).',
        inTextCitation: "(World Health Organization 2024)",
      },
      {
        sourceType: "Book Chapter",
        referenceEntry:
          'Jones, Maria. 2024. "Research Methods in Sociology." Pp. 55–80 in Handbook of Sociological Research, edited by S. Brown. New York: University Press.',
        inTextCitation: "(Jones 2024:60)",
      },
      {
        sourceType: "Newspaper Article",
        referenceEntry:
          'Garcia, Carlos. 2024. "New Trends in Academic Citation." New York Times, March 15, p. A1.',
        inTextCitation: "(Garcia 2024)",
      },
    ],
    annotatedBibRules: [
      "Each entry begins with a standard ASA reference entry, followed by the annotation",
      "Annotations are typically 100–200 words and appear as an indented paragraph below the reference",
      "Use a hanging indent for the reference entry",
      "ASA references begin with the author's last name and the year — the year comes second, not after the title",
      "Annotations may be descriptive (summarize the source) or evaluative (assess its relevance and quality)",
    ],
    referencePageRules: [
      'Title the page "References" (centered) at the top',
      "Start on a new page after the body of the paper",
      "Use a hanging indent for each entry",
      "Double-space all entries",
      "Alphabetize by the first author's last name",
      "The year of publication follows the author's name immediately, before the title",
      "Use a colon before page numbers in in-text citations (Smith 2024:15), not 'p.'",
    ],
  },
  {
    slug: "apsa",
    name: "APSA",
    fullName: "APSA Style Manual",
    edition: "current",
    referenceListName: "References",
    paperFormat: {
      font: "12 pt Times New Roman or similar serif font",
      fontSize: "12 pt",
      margins: "1 inch on all sides",
      spacing: "Double-spaced throughout, including the reference list",
      titlePage:
        "Separate title page with title, author name and affiliation, contact information, acknowledgments, and abstract (150 words max)",
      headers: "Page numbers in the upper-right corner",
    },
    inTextRules: {
      singleAuthor: "(Smith 2024)",
      twoAuthors: "(Smith and Jones 2024)",
      threeOrMore: "(Smith et al. 2024) for 3+ authors",
      directQuote: "(Smith 2024, 15) — use a comma before the page number",
      noAuthor: '("Article Title" 2024)',
      noDate: "(Smith n.d.)",
    },
    examples: [
      {
        sourceType: "Book",
        referenceEntry:
          "Smith, Jane D. 2024. The Art of Research. London: Academic Press.",
        inTextCitation: "(Smith 2024)",
      },
      {
        sourceType: "Journal Article",
        referenceEntry:
          'Lee, Alex B., and Chris Park. 2024. "Modern Citation Practices." Journal of Academic Writing 15(2): 45–67.',
        inTextCitation: "(Lee and Park 2024, 50)",
      },
      {
        sourceType: "Website",
        referenceEntry:
          'World Health Organization. 2024. "Global Health Statistics." https://www.who.int/statistics (March 20, 2024).',
        inTextCitation: "(World Health Organization 2024)",
      },
      {
        sourceType: "Government Document",
        referenceEntry:
          'United States Congress. 2024. "Congressional Research Report on Citation Standards." Washington, DC: Government Printing Office.',
        inTextCitation: "(United States Congress 2024)",
      },
      {
        sourceType: "Book Chapter",
        referenceEntry:
          'Jones, Maria. 2024. "Electoral Systems and Representation." In Handbook of Political Science, ed. Sarah Brown. Oxford: Oxford University Press, 55–80.',
        inTextCitation: "(Jones 2024, 60)",
      },
    ],
    annotatedBibRules: [
      "Each entry begins with a standard APSA reference entry, followed by the annotation",
      "Annotations are typically 100–200 words and appear as an indented paragraph below the reference",
      "Use a hanging indent for the reference entry",
      "APSA follows a similar format to ASA and APA but with political science-specific conventions",
      "Evaluate sources for their theoretical framework, methodology, and contribution to political science",
    ],
    referencePageRules: [
      'Title the page "References" (centered) at the top',
      "Start on a new page after the body of the paper",
      "Use a hanging indent for each entry",
      "Double-space all entries",
      "Alphabetize by the first author's last name",
      "The year follows the author's name before the title",
      "For journal articles, include the volume number, issue number in parentheses, and page range",
    ],
  },
];

export function getDetailedStyle(slug: string): DetailedStyleData | undefined {
  return DETAILED_STYLES.find((s) => s.slug === slug);
}
